/**
 * Post-login return target — capture and consume (PROD-2366).
 *
 * THE BUG THIS REPLACES. The unauthenticated-redirect guard used to stash
 * `location.pathname` only:
 *
 *     <Navigate to="/login" replace state={{ from: location.pathname }} />
 *
 * so every query parameter and the hash were dropped at the door. A cold open
 * of `/?date=2026-09-16&view=Day&actor=devices` came back from `/login` as
 * plain `/`, and the Devices calendar silently rendered as the Practitioner
 * calendar.
 *
 * WHY IT LOOKED LIKE AN ALLOW-LIST, AND IS NOT. In the field only `actor`
 * appeared to be lost while `date` and `view` "survived" — the classic
 * signature of a return URL rebuilt from a list of known keys. It is not:
 * `date` and `view` were never carried either. The calendar mirrors its own
 * state into the URL on mount (`scheduling-page.tsx`), so after landing on a
 * bare `/` it immediately re-wrote `?date=<today>&view=Day` from its own
 * defaults. The reported link asked for today's date in Day view, so the
 * re-written defaults happened to match the requested values and looked
 * preserved. `actor` had no such coincidence: Practitioners is encoded as the
 * ABSENCE of the param (`calendar-actor.ts`), so the re-write deleted it.
 *
 * Adding `actor` to some list of carried keys would therefore have fixed
 * nothing structural and left the next parameter to be introduced broken in
 * exactly the same way. The whole location — pathname + search + hash — is
 * round-tripped instead, so no future parameter has to be registered here.
 */

import type { Location } from 'react-router-dom';

/** Where an unauthenticated visitor lands when there is no usable target. */
export const DEFAULT_RETURN_TO = '/';

/**
 * Narrow the fields we actually read, so callers can pass a react-router
 * `Location`, a `window.location`, or a plain object in a test.
 */
export type ReturnToSource = Pick<Location, 'pathname' | 'search' | 'hash'>;

/**
 * Serialise the location the visitor actually asked for into the string that
 * rides in the router's `state.from` across the login round trip.
 *
 * The whole query string and the hash are kept verbatim — no key list, no
 * re-encoding, no re-ordering.
 */
export function captureReturnTo(loc: ReturnToSource): string {
  return sanitizeReturnTo(
    `${loc.pathname ?? ''}${loc.search ?? ''}${loc.hash ?? ''}`,
  );
}

/**
 * Control characters, including tab / newline / carriage return, which
 * browsers strip from a URL BEFORE parsing its scheme — so a target carrying
 * them cannot be judged by reading it literally. Scanned rather than matched
 * with a regex so the source carries no control-character escapes.
 */
function hasControlChar(value: string): boolean {
  for (let j = 0; j < value.length; j += 1) {
    const code = value.charCodeAt(j);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * Validate a return target before navigating to it.
 *
 * SECURITY (open redirect). `state.from` is not attacker-supplied today — the
 * guard writes it from the visitor's own location and it never comes off the
 * query string — but a return target that survives an auth boundary is the
 * classic open-redirect carrier, and no such guard existed before this change.
 * Everything that is not a SAME-ORIGIN, root-relative path is refused and
 * replaced with `/`:
 *
 *   - an absolute URL with a scheme (`https://evil.example/x`)
 *   - a protocol-relative target (`//evil.example/x`) — the browser treats
 *     this as another origin even though it "looks" like a path
 *   - the backslash variant (`/` then `\evil.example/x`) browsers normalise
 *     into the protocol-relative form
 *   - non-navigational schemes (`javascript:`, `data:`)
 *   - anything not starting with `/` — a relative path can escape the
 *     intended surface once resolved
 *   - anything containing a control character (see `hasControlChar`)
 *
 * Returns the path unchanged when it is safe, `fallback` otherwise.
 */
export function sanitizeReturnTo(
  raw: unknown,
  fallback: string = DEFAULT_RETURN_TO,
): string {
  if (typeof raw !== 'string') return fallback;
  if (raw === '') return fallback;
  if (hasControlChar(raw)) return fallback;
  if (!raw.startsWith('/')) return fallback;
  // `//host` and `/\host` leave the origin despite the leading slash.
  if (raw.startsWith('//') || raw.startsWith('/\\')) return fallback;
  return raw;
}

/**
 * Read the return target out of a router `location.state`, guarded.
 *
 * Every consumer of the login round trip goes through this, so the
 * open-redirect check cannot be forgotten at one of the call sites.
 */
export function readReturnTo(
  state: unknown,
  fallback: string = DEFAULT_RETURN_TO,
): string {
  const from =
    state && typeof state === 'object'
      ? (state as { from?: unknown }).from
      : undefined;
  return sanitizeReturnTo(from, fallback);
}
